// Servicio para manejar las operaciones relacionadas con los cupones activos

import db from "../db/database";
import { fetchFromBusinessCentralEspecial } from "../utils/businessCentralUtils";
import { isOnline } from "./syncService";

// Obtener todos los cupones activos - prioriza servidor si hay internet
export const getAllActiveCoupons = async () => {
  try {
    // Si hay internet, intentar obtener desde Business Central primero
    if (isOnline()) {
      try {
        const response = await fetchFromBusinessCentralEspecial(`Importaciones_getactivecoupons`);
        const serverCoupons = JSON.parse(response).Active_Coupons;

        // Guardar en la base de datos local para uso offline
        await saveCouponsToLocalDB(serverCoupons);

        return serverCoupons;
      } catch (serverError) {
        console.warn("Error al obtener cupones del servidor, usando datos locales:", serverError);
        // Si falla el servidor, continuar con datos locales
      }
    } else {
    }

    // Fallback: obtener desde la base de datos local
    const localCoupons = await db.coupons
      .filter((coupon) => !coupon.deleted && coupon.Active)
      .toArray();

    // Si hay cupones locales, devolverlos con sus detalles
    if (localCoupons.length > 0) {
      const couponsWithDetails = await Promise.all(
        localCoupons.map(async (coupon) => {
          // Obtener detalles del cupón
          const couponDetails = await db.couponDetails
            .where("couponNo")
            .equals(coupon.No)
            .toArray();

          // Obtener precios de venta
          const salesPrices = await db.couponSalesPrices
            .where("couponNo")
            .equals(coupon.No)
            .toArray();

          // Obtener descuentos por línea
          const lineDiscounts = await db.couponLineDiscounts
            .where("couponNo")
            .equals(coupon.No)
            .toArray();

          return {
            ...coupon,
            Coupon_Details: couponDetails.map((detail) => ({
              Detail_Type: detail.Detail_Type,
              No: detail.No,
              Discount_Percentage: detail.Discount_Percentage,
              Discount_Amount: detail.Discount_Amount,
              Total_Discounted_Amount: detail.Total_Discounted_Amount,
              Status: detail.Status,
            })),
            Sales_Prices: salesPrices.map((price) => ({
              Item_No: price.Item_No,
              Unit_Price: price.Unit_Price,
              Currency_Code: price.Currency_Code,
              Starting_Date: price.Starting_Date,
              Ending_Date: price.Ending_Date,
              Minimum_Quantity: price.Minimum_Quantity,
              Unit_of_Measure_Code: price.Unit_of_Measure_Code,
              Variant_Code: price.Variant_Code,
            })),
            Line_Discounts: lineDiscounts.map((discount) => ({
              Type: discount.Type,
              Code: discount.Code,
              Line_Discount_Percentage: discount.Line_Discount_Percentage,
              Currency_Code: discount.Currency_Code,
              Starting_Date: discount.Starting_Date,
              Ending_Date: discount.Ending_Date,
              Minimum_Quantity: discount.Minimum_Quantity,
              Unit_of_Measure_Code: discount.Unit_of_Measure_Code,
              Variant_Code: discount.Variant_Code,
            })),
          };
        })
      );

      return couponsWithDetails;
    }

    // Si no hay cupones locales ni del servidor, devolver array vacío
    return [];
  } catch (error) {
    console.error("Error al obtener cupones activos:", error);
    throw error;
  }
};

// Función para guardar cupones en la base de datos local
export const saveCouponsToLocalDB = async (coupons) => {
  try {
    // Limpiar datos anteriores
    await db.coupons.clear();
    await db.couponDetails.clear();
    await db.couponSalesPrices.clear();
    await db.couponLineDiscounts.clear();

    for (const coupon of coupons) {
      // Guardar cupón principal
      await db.coupons.add({
        No: coupon.No,
        Description: coupon.Description,
        Starting_Date: coupon.Starting_Date,
        Ending_Date: coupon.Ending_Date,
        Status: coupon.Status,
        Active: coupon.Active,
        Discount_Percentage: coupon.Discount_Percentage,
        Discount_Amount: coupon.Discount_Amount,
        Total_Discounted_Amount: coupon.Total_Discounted_Amount,
        pendingSync: false,
        lastModified: new Date(),
        lastSynced: new Date(),
        deleted: false,
      });

      // Guardar detalles del cupón
      if (coupon.Coupon_Details && coupon.Coupon_Details.length > 0) {
        for (const detail of coupon.Coupon_Details) {
          await db.couponDetails.add({
            couponNo: coupon.No,
            Detail_Type: detail.Detail_Type,
            No: detail.No,
            Discount_Percentage: detail.Discount_Percentage,
            Discount_Amount: detail.Discount_Amount,
            Total_Discounted_Amount: detail.Total_Discounted_Amount,
            Status: detail.Status,
            pendingSync: false,
            lastModified: new Date(),
            lastSynced: new Date(),
            deleted: false,
          });
        }
      }

      // Guardar precios de venta
      if (coupon.Sales_Prices && coupon.Sales_Prices.length > 0) {
        for (const price of coupon.Sales_Prices) {
          await db.couponSalesPrices.add({
            couponNo: coupon.No,
            Item_No: price.Item_No,
            Unit_Price: price.Unit_Price,
            Currency_Code: price.Currency_Code,
            Starting_Date: price.Starting_Date,
            Ending_Date: price.Ending_Date,
            Minimum_Quantity: price.Minimum_Quantity,
            Unit_of_Measure_Code: price.Unit_of_Measure_Code,
            Variant_Code: price.Variant_Code,
            pendingSync: false,
            lastModified: new Date(),
            lastSynced: new Date(),
            deleted: false,
          });
        }
      }

      // Guardar descuentos por línea
      if (coupon.Line_Discounts && coupon.Line_Discounts.length > 0) {
        for (const discount of coupon.Line_Discounts) {
          await db.couponLineDiscounts.add({
            couponNo: coupon.No,
            Type: discount.Type,
            Code: discount.Code,
            Line_Discount_Percentage: discount.Line_Discount_Percentage,
            Currency_Code: discount.Currency_Code,
            Starting_Date: discount.Starting_Date,
            Ending_Date: discount.Ending_Date,
            Minimum_Quantity: discount.Minimum_Quantity,
            Unit_of_Measure_Code: discount.Unit_of_Measure_Code,
            Variant_Code: discount.Variant_Code,
            pendingSync: false,
            lastModified: new Date(),
            lastSynced: new Date(),
            deleted: false,
          });
        }
      }
    }
  } catch (error) {
    console.error("Error al guardar cupones en la base de datos local:", error);
    throw error;
  }
};

// Obtener cupón por número desde la base de datos local
export const getCouponByNo = async (no) => {
  try {
    const coupon = await db.coupons
      .where("No")
      .equals(no)
      .and((coupon) => !coupon.deleted)
      .first();

    // Que no exista no es un error, es un resultado. El dashboard consulta cada
    // código de descuento primero como cupón y después como campaña, así que
    // cualquier campaña producía acá una traza roja de algo que estaba
    // funcionando: en el ambiente, "2026-2" es una campaña, y su búsqueda como
    // cupón fallaba por diseño. Se devuelve null y el llamador decide.
    if (!coupon) {
      return null;
    }

    // Obtener detalles del cupón
    const couponDetails = await db.couponDetails.where("couponNo").equals(coupon.No).toArray();

    const salesPrices = await db.couponSalesPrices.where("couponNo").equals(coupon.No).toArray();

    const lineDiscounts = await db.couponLineDiscounts
      .where("couponNo")
      .equals(coupon.No)
      .toArray();

    return {
      ...coupon,
      Coupon_Details: couponDetails,
      Sales_Prices: salesPrices,
      Line_Discounts: lineDiscounts,
    };
  } catch (error) {
    console.error(`Error al obtener cupón con número ${no}:`, error);
    throw error;
  }
};

// Obtener cupón por descripción desde la base de datos local
export const getCouponByDescription = async (description) => {
  try {
    const coupons = await db.coupons
      .filter((coupon) => !coupon.deleted && coupon.Description === description)
      .toArray();

    return coupons;
  } catch (error) {
    console.error(`Error al obtener cupón con descripción ${description}:`, error);
    throw error;
  }
};

// Buscar cupones por texto en la base de datos local
export const searchCoupons = async (searchText) => {
  try {
    if (!searchText) {
      return await getAllActiveCoupons();
    }

    const coupons = await db.coupons
      .filter(
        (coupon) =>
          !coupon.deleted &&
          (coupon.Description?.toLowerCase().includes(searchText.toLowerCase()) ||
            coupon.No?.toLowerCase().includes(searchText.toLowerCase()) ||
            coupon.Status?.toLowerCase().includes(searchText.toLowerCase()))
      )
      .toArray();

    // Agregar detalles a cada cupón encontrado
    const couponsWithDetails = await Promise.all(
      coupons.map(async (coupon) => {
        const couponDetails = await db.couponDetails.where("couponNo").equals(coupon.No).toArray();

        const salesPrices = await db.couponSalesPrices
          .where("couponNo")
          .equals(coupon.No)
          .toArray();

        const lineDiscounts = await db.couponLineDiscounts
          .where("couponNo")
          .equals(coupon.No)
          .toArray();

        return {
          ...coupon,
          Coupon_Details: couponDetails,
          Sales_Prices: salesPrices,
          Line_Discounts: lineDiscounts,
        };
      })
    );

    return couponsWithDetails;
  } catch (error) {
    console.error(`Error al buscar cupones con texto ${searchText}:`, error);
    throw error;
  }
};

// Obtener cupones por estado desde la base de datos local
export const getCouponsByStatus = async (status) => {
  try {
    const coupons = await db.coupons
      .filter((coupon) => !coupon.deleted && coupon.Status === status)
      .toArray();

    // Agregar detalles a cada cupón encontrado
    const couponsWithDetails = await Promise.all(
      coupons.map(async (coupon) => {
        const couponDetails = await db.couponDetails.where("couponNo").equals(coupon.No).toArray();

        const salesPrices = await db.couponSalesPrices
          .where("couponNo")
          .equals(coupon.No)
          .toArray();

        const lineDiscounts = await db.couponLineDiscounts
          .where("couponNo")
          .equals(coupon.No)
          .toArray();

        return {
          ...coupon,
          Coupon_Details: couponDetails,
          Sales_Prices: salesPrices,
          Line_Discounts: lineDiscounts,
        };
      })
    );

    return couponsWithDetails;
  } catch (error) {
    console.error(`Error al obtener cupones con estado ${status}:`, error);
    throw error;
  }
};

// Obtener cupones activos solamente desde la base de datos local
export const getOnlyActiveCoupons = async () => {
  try {
    const coupons = await db.coupons
      .filter((coupon) => !coupon.deleted && coupon.Active === true)
      .toArray();

    // Agregar detalles a cada cupón encontrado
    const couponsWithDetails = await Promise.all(
      coupons.map(async (coupon) => {
        const couponDetails = await db.couponDetails.where("couponNo").equals(coupon.No).toArray();

        const salesPrices = await db.couponSalesPrices
          .where("couponNo")
          .equals(coupon.No)
          .toArray();

        const lineDiscounts = await db.couponLineDiscounts
          .where("couponNo")
          .equals(coupon.No)
          .toArray();

        return {
          ...coupon,
          Coupon_Details: couponDetails,
          Sales_Prices: salesPrices,
          Line_Discounts: lineDiscounts,
        };
      })
    );

    return couponsWithDetails;
  } catch (error) {
    console.error("Error al obtener cupones activos:", error);
    throw error;
  }
};
